import React, { useState } from "react"
import { CustomForm } from "ui/Form"
import PropTypes from "prop-types"
import styles from "./EditPricesForm.module.css"
import { PRICING_TYPES, TRIP_PRICE } from "../../utils/constants"
import { ActionButtons } from "../ActionButtons"
import { DurationFormItems } from "../DurationFormItems"
import { FlatRateFormItems } from "../FlatRateFormItems"
import { PerPersonFormItems } from "../PerPersonFormItems"
import { serialize } from "object-to-formdata"
import { useDispatch, useSelector } from "react-redux"
import { updateTrip, getGuideTrip } from "store/trips"
import { useParams } from "react-router-dom"
import { ConfirmModal } from "ui/ConfirmModal"
import { useTranslation } from "react-i18next"
import { modelSelector } from "store/selectors"
import { Spin } from "antd"
export const EditPricesForm = ({ record, closeEditForm, isFlatRate, setDraftPrices, addNewPrice, form, expandedRowData, onPriceInteraction }) => {

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const trip = useSelector(modelSelector("trip"))
  const [loading, setLoading] = useState(false)
  const [isConfirmOpened, setIsConfirmOpened] = useState(false)
  const { id } = useParams()
  const isPriceSaved = !!expandedRowData?.pricing_type

  const savePrice = (callback) => {
    setLoading(true)
    form
      .validateFields()
      .then(() => {
        const allFormValues = form.getFieldsValue(true)

        // Extract trip-level fields (min_guests, max_guests) from price fields
        const currentDuration = allFormValues[TRIP_PRICE.DURATION]
        const existingPrices = trip?.trip_prices || []
        const currentRecordId = isPriceSaved ? record?.id : null
        const hasDuplicate = existingPrices.some(
          (p) => p.trip_duration === currentDuration && p.id !== currentRecordId
        )

        if (hasDuplicate) {
          form.setFields([{ name: TRIP_PRICE.DURATION, errors: [t("experiences.scheduling.errors.duplicate_duration")] }])
          setLoading(false)
          return
        }

        const { min_guests, max_guests, ...priceFields } = allFormValues

        const priceObj = {
          ...priceFields,
          id: record?.id
        }

        if (!isPriceSaved) {
          delete priceObj.id
        }

        const formData = serialize(
          {
            trip: {
              pricing_type: isFlatRate ? PRICING_TYPES.FLAT_RATE : PRICING_TYPES.PER_PERSON,
              trip_prices_attributes: [priceObj],
              ...(min_guests !== undefined && { min_guests }),
              ...(max_guests !== undefined && { max_guests }),
              available_days: trip?.available_days,
              available_hours: trip?.available_hours
                ? trip.available_hours
                  .filter(({ start, end }) => start && end)
                  .map(({ start, end }) => ({
                    start: typeof start === "string" ? start : start.format("HH:mm"),
                    end: typeof end === "string" ? end : end.format("HH:mm")
                  }))
                : [],
              trip_slots_attributes: trip?.available_hours
                ? trip.available_hours
                  .filter(({ start, end }) => start && end)
                  .map(({ start, end }) => ({
                    start_time: typeof start === "string" ? start : start.format("HH:mm"),
                    end_time: typeof end === "string" ? end : end.format("HH:mm")
                  }))
                : []
            }
          },
          { allowEmptyArrays: true }
        )
        const afterUpdate = () => {
          dispatch(getGuideTrip(id)).finally(() => {
            if (callback) callback();
            setLoading(false);
          });
        };
        const handleError = () => {
          setLoading(false);
        };
        dispatch(updateTrip(id, formData))
          .then(afterUpdate)
          .catch(handleError);
      })
      .catch((error) => {
        setLoading(false)
        console.error("Form validation failed:", error)
      })
  }

  const deletePrice = () => {
    if (isPriceSaved) {
      const formData = serialize(
        {
          trip: {
            trip_prices_attributes: [
              {
                ...form.getFieldsValue(true),
                id: record?.id,
                pricing_type: isFlatRate ? PRICING_TYPES.FLAT_RATE : PRICING_TYPES.PER_PERSON,
                _destroy: true
              }
            ]
          }
        },
        { allowEmptyArrays: true }
      )
      dispatch(updateTrip(id, formData)).then().catch(() => {
        setLoading(false)
      })
    } else {
      setDraftPrices((prev) => [...prev].filter((draftPrice) => draftPrice.id !== record.id))
    }
    onPriceInteraction?.()
  }

  const closeDeleteConfirmModal = () => {
    setIsConfirmOpened(false)
  }

  const openDeleteConfirmModal = () => {
    setIsConfirmOpened(true)
  }

  const onSave = () => {
    if (isPriceSaved) {
      savePrice(() => {
        setLoading(false)
      })
    } else {
      savePrice(deletePrice)
    }
  }

  const onSaveAndAddNew = () => {
    if (isPriceSaved) {
      savePrice(() => {
        closeEditForm()
        addNewPrice()
        setLoading(false)
      })
    } else {
      savePrice(() => {
        setLoading(false)
      })
    }
  }

  return (

    <Spin spinning={loading}>
      <CustomForm form={form} layout="vertical" autoComplete="off" className={styles.wrapper} initialValues={record} onValuesChange={() => onPriceInteraction?.()}>
        <DurationFormItems form={form} expandedRowData={expandedRowData} />
        {isFlatRate ? <FlatRateFormItems form={form} record={expandedRowData} /> : <PerPersonFormItems form={form} record={expandedRowData} />}
        <ActionButtons onSave={onSave} onSaveAndAddNew={onSaveAndAddNew} onCancel={closeEditForm} onDelete={openDeleteConfirmModal} />
      </CustomForm>
      <ConfirmModal
        open={isConfirmOpened}
        title={t("experiences.scheduling.sections.length_and_rates.delete_duration_title")}
        info={t("experiences.scheduling.sections.length_and_rates.delete_duration_info")}
        onCancel={closeDeleteConfirmModal}
        onDelete={deletePrice}
      />
    </Spin>

  )
}

EditPricesForm.propTypes = {
  record: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pricing_type: PropTypes.string,
  }).isRequired,
  closeEditForm: PropTypes.func.isRequired,
  isFlatRate: PropTypes.bool,
  setDraftPrices: PropTypes.func.isRequired,
  addNewPrice: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  expandedRowData: PropTypes.object,
  onPriceInteraction: PropTypes.func
};
